import type { Socket } from 'phoenix';
import { createLink } from 'slab-apollo-absinthe-upload-link';
import * as AbsintheSocket from '@absinthe/socket';
import { createAbsintheSocketLink } from '@absinthe/socket-apollo-link';
import { ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import extensionFetchProxy from '../../utils/extensionFetchProxy';
import isExtensionPath from '../../utils/isExtensionPath';

export const CONTEXT_AUTH_TOKEN_KEY = 'context.authToken';

const abortController = new AbortController();

const getHttpLink = () => {
  const { host, pathname } = window.location;
  const isExtension = isExtensionPath(pathname);

  return createLink({
    fetchOptions: {
      // Avoid noisy exception reports in Sentry.
      // This will not be needed anymore with Apollo >= 3.8 (https://github.com/apollographql/apollo-client/pull/11040).
      signal: abortController.signal,
    },
    fetch: isExtension ? extensionFetchProxy : undefined,
    uri: (operation) =>
      `https://${host}/graphql?op=${encodeURIComponent(operation.operationName)}`,
  });
};

const getQueryAndMutationLink = (authToken: string | null) =>
  ApolloLink.concat(
    setContext((_operation, context) => {
      const authorization = context?.[CONTEXT_AUTH_TOKEN_KEY] ?? authToken;
      return { headers: authorization ? { authorization } : {} };
    }),
    getHttpLink(),
  );

const getSubscriptionLink = (socket: Socket | null) =>
  socket
    ? (createAbsintheSocketLink(
        AbsintheSocket.create(socket),
      ) as unknown as ApolloLink) // Absinthe socket library uses a deprecated ApolloLink library
    : ApolloLink.empty();

type GetTransportLinkOptions =
  | { authToken: string; socket: Socket }
  | { authToken: null; socket: null };

const getTransportLink = ({ authToken, socket }: GetTransportLinkOptions) =>
  ApolloLink.split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return (
        def.kind === 'OperationDefinition' && def.operation === 'subscription'
      );
    },
    getSubscriptionLink(socket),
    getQueryAndMutationLink(authToken),
  );

export default getTransportLink;
